import React, { useState, useEffect } from 'react';
import AddSilkscreenModal from '../modals/AddSilkscreenModal';
import EditSilkscreenModal from '../modals/EditSilkscreenModal';
import CustomerManager from '../CustomerManager';
import CheckoutProductionModal from '../modals/CheckoutProductionModal';
import CheckoutSupplierModal from '../modals/CheckoutSupplierModal';
import CheckinModal from '../modals/CheckinModal';
import QualityApprovalModal from '../modals/QualityApprovalModal';
import UpdateCoveringModal from '../modals/UpdateCoveringModal';
import ReimposeModal from '../modals/ReimposeModal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4001';

function SilkscreensPage() {
  const [statusSummary, setStatusSummary] = useState({
    available: 0,
    in_production: 0,
    at_supplier: 0,
    quality_hold: 0
  });
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [screens, setScreens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showCustomerManager, setShowCustomerManager] = useState(false);
  const [newlyAddedCustomerId, setNewlyAddedCustomerId] = useState(null);
  const [enlargedPreview, setEnlargedPreview] = useState(null);
  const [selectedScreen, setSelectedScreen] = useState(null);
  const [showCheckoutProduction, setShowCheckoutProduction] = useState(false);
  const [showCheckoutSupplier, setShowCheckoutSupplier] = useState(false);
  const [showCheckin, setShowCheckin] = useState(false);
  const [showQualityApproval, setShowQualityApproval] = useState(false);
  const [showUpdateCovering, setShowUpdateCovering] = useState(false);
  const [showReimpose, setShowReimpose] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showScreenDetails, setShowScreenDetails] = useState(false);
  const [screenDetails, setScreenDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    fetchStatusSummary();
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await fetch(`${API_URL}/clients`);
      const data = await response.json();
      setCustomers(data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const addNotification = (message) => {
    setNotifications([message]);
    setTimeout(() => {
      setNotifications([]);
    }, 5000);
  };


  const fetchStatusSummary = async () => {
    try {
      const response = await fetch(`${API_URL}/silkscreens/status-summary`);
      const data = await response.json();
      setStatusSummary(data);
      
      if (data.quality_hold > 0) {
        setNotifications([`${data.quality_hold} screen${data.quality_hold > 1 ? 's' : ''} on quality hold requiring approval`]);
      }
    } catch (error) {
      console.error('Error fetching status summary:', error);
    }
  };

  const fetchScreensByStatus = async (status) => {
    setSelectedStatus(status);
    setSelectedCustomer('');
    setScreens([]);
  };

  const fetchScreensByCustomer = async (customerId) => {
    if (!selectedStatus || !customerId) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/silkscreens/by-status/${selectedStatus}`);
      const data = await response.json();
      
      let filtered = data;
      if (customerId !== 'ALL') {
        filtered = data.filter(screen => screen.currentCustomerName === customers.find(c => c.id === customerId)?.name);
      }
      
      setScreens(filtered);
    } catch (error) {
      console.error('Error fetching screens:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddScreen = () => {
    setShowAddModal(true);
  };

  const handleScreenAdded = () => {
    setShowAddModal(false);
    fetchStatusSummary();
    if (selectedStatus) {
      fetchScreensByStatus(selectedStatus);
    }
    addNotification('Silkscreen added successfully');
  };

  const handleCheckoutProduction = (screen) => {
    setSelectedScreen(screen);
    setShowCheckoutProduction(true);
  };

  const handleCheckoutSupplier = (screen) => {
    setSelectedScreen(screen);
    setShowCheckoutSupplier(true);
  };

  const handleCheckin = (screen) => {
    setSelectedScreen(screen);
    setShowCheckin(true);
  };

  const handleCheckoutSuccess = () => {
    setShowCheckoutProduction(false);
    setShowCheckoutSupplier(false);
    setSelectedScreen(null);
    fetchStatusSummary();
    if (selectedCustomer) {
      fetchScreensByCustomer(selectedCustomer);
    }
    addNotification('Screen checked out successfully');
  };

  const handleCheckinSuccess = () => {
    setShowCheckin(false);
    setSelectedScreen(null);
    fetchStatusSummary();
    if (selectedCustomer) {
      fetchScreensByCustomer(selectedCustomer);
    }
    addNotification('Screen checked in successfully');
  };

  const handleQualityApproval = (screen) => {
    setSelectedScreen(screen);
    setShowQualityApproval(true);
  };

  const handleUpdateCovering = (screen) => {
    setSelectedScreen(screen);
    setShowUpdateCovering(true);
  };

  const handleReimpose = (screen) => {
    setSelectedScreen(screen);
    setShowReimpose(true);
  };

  const handleQualityApprovalSuccess = () => {
    setShowQualityApproval(false);
    setSelectedScreen(null);
    fetchStatusSummary();
    if (selectedCustomer) {
      fetchScreensByCustomer(selectedCustomer);
    }
    addNotification('Quality approval added successfully');
  };

  const handleUpdateCoveringSuccess = () => {
    setShowUpdateCovering(false);
    setSelectedScreen(null);
    if (selectedCustomer) {
      fetchScreensByCustomer(selectedCustomer);
    }
    addNotification('Covering status updated successfully');
  };

  const handleReimposeSuccess = () => {
    setShowReimpose(false);
    setSelectedScreen(null);
    fetchStatusSummary();
    if (selectedCustomer) {
      fetchScreensByCustomer(selectedCustomer);
    }
    addNotification('Screen re-imposed successfully');
  };

  const fetchScreenDetails = async (screenId) => {
    setLoadingDetails(true);
    try {
      const response = await fetch(`${API_URL}/silkscreens/${screenId}`);
      const data = await response.json();
      setScreenDetails(data);
      setShowScreenDetails(true);
    } catch (err) {
      console.error('Error fetching screen details:', err);
    } finally {
      setLoadingDetails(false);
    }
  };

  const closeScreenDetails = () => {
    setShowScreenDetails(false);
    setScreenDetails(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return '#4CAF50';
      case 'in_production': return '#2196F3';
      case 'at_supplier': return '#FF9800';
      case 'quality_hold': return '#F44336';
      default: return '#9E9E9E';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'available': return 'Available';
      case 'in_production': return 'In Production';
      case 'at_supplier': return 'At Supplier';
      case 'quality_hold': return 'Quality Hold';
      default: return status;
    }
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px', position: 'relative' }}>
        <h1 style={{ 
          fontSize: '2rem', 
          fontWeight: '800', 
          color: '#00BCD4',
          marginBottom: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          🖼️ Silkscreens Management
        </h1>
        <p style={{ color: '#666', fontSize: '1rem' }}>
          Track and manage silkscreen inventory, usage, and quality
        </p>
        
        {/* User Manual Button - Top Right */}
        <button
          onClick={() => window.open('/docs/manuals/silkscreen/current/SILKSCREEN_MANUAL_v1.0.html', '_blank')}
          style={{
            position: 'absolute',
            top: '0',
            right: '0',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '8px',
            fontSize: '0.95rem',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
          }}
        >
          📖 User Manual
        </button>
      </div>

      {/* Notification Banner */}
      {notifications.length > 0 && (
        <div style={{
          background: '#FF9800',
          color: 'white',
          padding: '12px 24px',
          borderRadius: '8px',
          marginBottom: '24px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <span style={{ fontSize: '1.2rem' }}>⚠️</span>
          <span style={{ fontWeight: '600' }}>{notifications[0]}</span>
        </div>
      )}

      {/* Status Blocks */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '32px'
      }}>
        {Object.entries(statusSummary).map(([status, count]) => (
          <div
            key={status}
            onClick={() => fetchScreensByStatus(status)}
            style={{
              background: `linear-gradient(135deg, ${getStatusColor(status)}dd 0%, ${getStatusColor(status)} 100%)`,
              borderRadius: '16px',
              padding: '24px',
              color: 'white',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: `0 4px 16px ${getStatusColor(status)}33`,
              transform: selectedStatus === status ? 'translateY(-4px)' : 'translateY(0)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = `0 8px 24px ${getStatusColor(status)}44`;
            }}
            onMouseLeave={(e) => {
              if (selectedStatus !== status) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = `0 4px 16px ${getStatusColor(status)}33`;
              }
            }}
          >
            <div style={{ fontSize: '3rem', fontWeight: '800', marginBottom: '8px' }}>
              {count}
            </div>
            <div style={{ fontSize: '1rem', fontWeight: '600', opacity: 0.9 }}>
              {getStatusLabel(status)}
            </div>
          </div>
        ))}
      </div>

      {/* Add New Button */}
      <div style={{ marginBottom: '24px' }}>
        <button
          onClick={handleAddScreen}
          style={{
            background: 'linear-gradient(135deg, #00BCD4 0%, #0097A7 100%)',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0, 188, 212, 0.3)',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 188, 212, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 188, 212, 0.3)';
          }}
        >
          + Add New Silkscreen
        </button>
      </div>

      {/* Screens List */}
      {selectedStatus && (
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px', gap: '16px' }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#333',
              margin: 0
            }}>
              {getStatusLabel(selectedStatus)} Screens
            </h2>
            <select
              value={selectedCustomer}
              onChange={(e) => {
                setSelectedCustomer(e.target.value);
                fetchScreensByCustomer(e.target.value);
              }}
              style={{
                fontSize: '15px',
                borderRadius: '8px',
                border: '2px solid #00BCD4',
                padding: '8px 16px',
                fontWeight: '600',
                color: '#00BCD4',
                background: '#f8fafc',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              <option value="">Select Customer</option>
              <option value="ALL">All Customers</option>
              {customers.map(cust => (
                <option key={cust.id} value={cust.id}>{cust.name}</option>
              ))}
            </select>
          </div>

          {!selectedCustomer ? (
            <div style={{ textAlign: 'center', padding: '60px', color: '#666' }}>
              <div style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px', color: '#00BCD4' }}>
                👆 Please select a customer to view screens
              </div>
              <div style={{ fontSize: '15px' }}>
                Choose "All Customers" to see all screens, or select a specific customer
              </div>
            </div>
          ) : loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#00BCD4' }}>
              Loading screens...
            </div>
          ) : screens.length === 0 ? (
            <p style={{ color: '#999', textAlign: 'center', padding: '32px' }}>
              No screens found
            </p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '0.95rem'
              }}>
                <thead>
                  <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #00BCD4' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '700', color: '#00BCD4' }}>Screen Code</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '700', color: '#00BCD4' }}>Customer</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '700', color: '#00BCD4' }}>Description</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: '700', color: '#00BCD4' }}>Layout</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: '700', color: '#00BCD4' }}>Covered</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: '700', color: '#00BCD4' }}>Quality</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: '700', color: '#00BCD4' }}>Preview</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: '700', color: '#00BCD4' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {screens.map((screen, idx) => (
                    <tr
                      key={screen.id}
                      style={{
                        borderBottom: '1px solid #e0e0e0',
                        background: idx % 2 === 0 ? 'white' : '#fafafa',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#e0f7fa'}
                      onMouseLeave={(e) => e.currentTarget.style.background = idx % 2 === 0 ? 'white' : '#fafafa'}
                    >
                      <td style={{ padding: '12px' }}>
                        <button
                          onClick={() => fetchScreenDetails(screen.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#00BCD4',
                            fontWeight: '700',
                            fontSize: '0.9rem',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            padding: 0
                          }}
                          onMouseOver={(e) => e.target.style.color = '#0097A7'}
                          onMouseOut={(e) => e.target.style.color = '#00BCD4'}
                        >
                          {screen.screenCode}
                        </button>
                      </td>
                      <td style={{ padding: '12px', color: '#333' }}>
                        {screen.currentCustomerName}
                      </td>
                      <td style={{ padding: '12px', color: '#666' }}>
                        {screen.currentItemDescription}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center', color: '#666' }}>
                        {screen.amountAcross} × {screen.amountAround}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        {screen.coveredPositionsCount > 0 ? (
                          <span style={{ color: '#FF9800', fontWeight: '600' }}>
                            {screen.coveredPositionsCount}
                          </span>
                        ) : (
                          <span style={{ color: '#4CAF50' }}>0</span>
                        )}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        {screen.qualityStatus === 'fail' ? (
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                            <span style={{
                              background: '#F44336',
                              color: 'white',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              fontWeight: '600'
                            }}>
                              ✗ Hold
                            </span>
                            {screen.qualityApprovals && screen.qualityApprovals.length > 0 && (
                              <span style={{
                                background: '#FF9800',
                                color: 'white',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontSize: '0.7rem',
                                fontWeight: '600'
                              }}>
                                {screen.qualityApprovals.length}/2 ✓
                              </span>
                            )}
                          </div>
                        ) : (
                          <span style={{ color: '#4CAF50' }}>✓</span>
                        )}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        {screen.artworkPreviewPath ? (
                          <img
                            src={`${API_URL}${screen.artworkPreviewPath}`}
                            alt="Preview"
                            onClick={() => setEnlargedPreview(screen.artworkPreviewPath)}
                            style={{
                              width: '60px',
                              height: '60px',
                              objectFit: 'contain',
                              borderRadius: '4px',
                              border: '1px solid #e0e0e0',
                              background: '#f5f5f5',
                              cursor: 'pointer',
                              transition: 'transform 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                          />
                        ) : (
                          <span style={{ color: '#999' }}>-</span>
                        )}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                          {screen.status === 'available' && (
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCheckoutProduction(screen);
                                }}
                                style={{
                                  padding: '6px 12px',
                                  background: '#2196F3',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  fontSize: '0.85rem',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  transition: 'background 0.2s'
                                }}
                                onMouseEnter={(e) => e.target.style.background = '#1976D2'}
                                onMouseLeave={(e) => e.target.style.background = '#2196F3'}
                              >
                                📤 Production
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCheckoutSupplier(screen);
                                }}
                                style={{
                                  padding: '6px 12px',
                                  background: '#FF9800',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  fontSize: '0.85rem',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  transition: 'background 0.2s'
                                }}
                                onMouseEnter={(e) => e.target.style.background = '#F57C00'}
                                onMouseLeave={(e) => e.target.style.background = '#FF9800'}
                              >
                                🚚 Supplier
                              </button>
                            </>
                          )}
                          {(screen.status === 'in_production' || screen.status === 'at_supplier') && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCheckin(screen);
                              }}
                              style={{
                                padding: '6px 12px',
                                background: '#4CAF50',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                fontSize: '0.85rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'background 0.2s'
                              }}
                              onMouseEnter={(e) => e.target.style.background = '#388E3C'}
                              onMouseLeave={(e) => e.target.style.background = '#4CAF50'}
                            >
                              📥 Check In
                            </button>
                          )}
                          {screen.status === 'quality_hold' && (
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCheckoutSupplier(screen);
                                }}
                                style={{
                                  padding: '6px 12px',
                                  background: '#FF9800',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  fontSize: '0.85rem',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  transition: 'background 0.2s'
                                }}
                                onMouseEnter={(e) => e.target.style.background = '#F57C00'}
                                onMouseLeave={(e) => e.target.style.background = '#FF9800'}
                              >
                                🚚 Supplier
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleQualityApproval(screen);
                                }}
                                style={{
                                  padding: '6px 12px',
                                  background: '#4CAF50',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  fontSize: '0.85rem',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  transition: 'background 0.2s'
                                }}
                                onMouseEnter={(e) => e.target.style.background = '#388E3C'}
                                onMouseLeave={(e) => e.target.style.background = '#4CAF50'}
                              >
                                ✓ Approve
                              </button>
                            </>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', marginTop: '8px' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedScreen(screen);
                              setShowEditModal(true);
                            }}
                            style={{
                              padding: '6px 12px',
                              background: '#9C27B0',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '0.85rem',
                              fontWeight: '600',
                              cursor: 'pointer',
                              transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#7B1FA2'}
                            onMouseLeave={(e) => e.target.style.background = '#9C27B0'}
                          >
                            ✏️ Edit
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUpdateCovering(screen);
                            }}
                            style={{
                              padding: '4px 8px',
                              background: '#FF9800',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              cursor: 'pointer',
                              transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#F57C00'}
                            onMouseLeave={(e) => e.target.style.background = '#FF9800'}
                            title="Update covering status"
                          >
                            🔴 Covering
                          </button>
                          {/* Re-impose button removed - now part of supplier checkout workflow */}
                          {false && screen.status === 'available' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReimpose(screen);
                              }}
                              style={{
                                padding: '4px 8px',
                                background: '#9C27B0',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'background 0.2s'
                              }}
                              onMouseEnter={(e) => e.target.style.background = '#7B1FA2'}
                              onMouseLeave={(e) => e.target.style.background = '#9C27B0'}
                              title="Re-impose with new artwork"
                            >
                              🔄 Re-impose
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Add Silkscreen Modal */}
      {showAddModal && (
        <AddSilkscreenModal
          onClose={() => setShowAddModal(false)}
          onSuccess={handleScreenAdded}
          customers={customers}
          onAddCustomer={() => setShowCustomerManager(true)}
          preSelectedCustomerId={newlyAddedCustomerId}
        />
      )}

      {/* Customer Manager Modal */}
      {showCustomerManager && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          padding: '20px'
        }}>
          <div style={{
            position: 'relative',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <button
              onClick={() => {
                setShowCustomerManager(false);
                setNewlyAddedCustomerId(null);
              }}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: '#F44336',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                fontSize: '1.2rem',
                cursor: 'pointer',
                zIndex: 1002,
                fontWeight: '700',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
              }}
            >
              ×
            </button>
            <CustomerManager
              customers={customers}
              items={[]}
              autoOpenAdd={true}
              onAdd={async (cust) => {
                const response = await fetch(`${API_URL}/clients`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(cust)
                });
                const newCustomer = await response.json();
                await fetchCustomers();
                setNewlyAddedCustomerId(newCustomer.id);
                setShowCustomerManager(false);
              }}
              onEdit={async (cust) => {
                await fetch(`${API_URL}/clients/${cust.id}`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(cust)
                });
                await fetchCustomers();
              }}
              onDelete={async (cust) => {
                await fetch(`${API_URL}/clients/${cust.id}`, {
                  method: 'DELETE'
                });
                await fetchCustomers();
              }}
            />
          </div>
        </div>
      )}

      {/* Enlarged Preview Modal */}
      {enlargedPreview && (
        <div
          onClick={() => setEnlargedPreview(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '20px',
            cursor: 'pointer'
          }}
        >
          <div style={{ position: 'relative', maxWidth: '90%', maxHeight: '90%' }}>
            <button
              onClick={() => setEnlargedPreview(null)}
              style={{
                position: 'absolute',
                top: '-40px',
                right: '0',
                background: '#F44336',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                fontSize: '1.5rem',
                cursor: 'pointer',
                fontWeight: '700',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                zIndex: 2001
              }}
            >
              ×
            </button>
            <img
              src={`${API_URL}${enlargedPreview}`}
              alt="Enlarged Preview"
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: '100%',
                maxHeight: '85vh',
                objectFit: 'contain',
                borderRadius: '8px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
                background: 'white',
                padding: '20px'
              }}
            />
          </div>
        </div>
      )}

      {/* Edit Silkscreen Modal */}
      {showEditModal && selectedScreen && (
        <EditSilkscreenModal
          screen={selectedScreen}
          customers={customers}
          onAddCustomer={() => {
            setShowEditModal(false);
            setShowCustomerManager(true);
          }}
          onClose={() => {
            setShowEditModal(false);
            setSelectedScreen(null);
          }}
          onSuccess={() => {
            fetchStatusSummary();
            if (selectedStatus) {
              fetchScreensByCustomer(selectedCustomer || 'ALL');
            }
            addNotification('Silkscreen updated successfully', 'success');
          }}
        />
      )}

      {/* Checkout Production Modal */}
      {showCheckoutProduction && selectedScreen && (
        <CheckoutProductionModal
          screen={selectedScreen}
          currentUser={null}
          onClose={() => {
            setShowCheckoutProduction(false);
            setSelectedScreen(null);
          }}
          onSuccess={handleCheckoutSuccess}
        />
      )}

      {/* Checkout Supplier Modal */}
      {showCheckoutSupplier && selectedScreen && (
        <CheckoutSupplierModal
          screen={selectedScreen}
          currentUser={null}
          customers={customers}
          onClose={() => {
            setShowCheckoutSupplier(false);
            setSelectedScreen(null);
          }}
          onSuccess={handleCheckoutSuccess}
        />
      )}

      {/* Checkin Modal */}
      {showCheckin && selectedScreen && (
        <CheckinModal
          screen={selectedScreen}
          currentUser={null}
          onClose={() => {
            setShowCheckin(false);
            setSelectedScreen(null);
          }}
          onSuccess={handleCheckinSuccess}
        />
      )}

      {/* Quality Approval Modal */}
      {showQualityApproval && selectedScreen && (
        <QualityApprovalModal
          screen={selectedScreen}
          currentUser={null}
          onClose={() => {
            setShowQualityApproval(false);
            setSelectedScreen(null);
          }}
          onSuccess={handleQualityApprovalSuccess}
        />
      )}

      {/* Update Covering Modal */}
      {showUpdateCovering && selectedScreen && (
        <UpdateCoveringModal
          screen={selectedScreen}
          onClose={() => {
            setShowUpdateCovering(false);
            setSelectedScreen(null);
          }}
          onSuccess={handleUpdateCoveringSuccess}
        />
      )}

      {/* Reimpose Modal */}
      {showReimpose && selectedScreen && (
        <ReimposeModal
          screen={selectedScreen}
          customers={customers}
          onClose={() => {
            setShowReimpose(false);
            setSelectedScreen(null);
          }}
          onSuccess={handleReimposeSuccess}
        />
      )}

      {/* Screen Details Modal */}
      {showScreenDetails && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }} onClick={closeScreenDetails}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            maxWidth: '1000px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
          }} onClick={(e) => e.stopPropagation()}>
            {loadingDetails ? (
              <div style={{ padding: '60px', textAlign: 'center' }}>
                <div style={{ fontSize: '2rem', marginBottom: '16px' }}>⏳</div>
                <p style={{ color: '#666' }}>Loading screen details...</p>
              </div>
            ) : screenDetails ? (
              <div style={{ padding: '32px' }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '24px' }}>
                  <div>
                    <h2 style={{ fontSize: '1.75rem', fontWeight: '800', color: '#00BCD4', marginBottom: '8px' }}>
                      {screenDetails.screenCode}
                    </h2>
                    <p style={{ color: '#666', fontSize: '0.95rem' }}>
                      {screenDetails.currentCustomerName} - {screenDetails.currentItemDescription}
                    </p>
                  </div>
                  <button
                    onClick={closeScreenDetails}
                    style={{
                      background: '#f44336',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    ✕ Close
                  </button>
                </div>

                {/* Main Content - Two Columns */}
                <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '24px', marginBottom: '24px' }}>
                  {/* Left Column - Screen Info */}
                  <div style={{ 
                    background: '#f5f5f5', 
                    padding: '20px', 
                    borderRadius: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px'
                  }}>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '4px' }}>Status</div>
                      <div style={{ fontWeight: '600', color: '#333' }}>{screenDetails.status}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '4px' }}>Quality</div>
                      <div style={{ fontWeight: '600', color: '#333' }}>{screenDetails.qualityStatus}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '4px' }}>Covered</div>
                      <div style={{ fontWeight: '600', color: '#333' }}>{screenDetails.covered ? 'Yes' : 'No'}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '4px' }}>Date Received</div>
                      <div style={{ fontWeight: '600', color: '#333' }}>
                        {new Date(screenDetails.dateReceived).toLocaleDateString()}
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Artwork Preview */}
                  <div style={{
                    background: '#f5f5f5',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px',
                    minHeight: '125px'
                  }}>
                    {screenDetails.artworkPreviewPath ? (
                      <img
                        src={`${API_URL}${screenDetails.artworkPreviewPath}`}
                        alt="Artwork Preview"
                        style={{
                          maxWidth: '85%',
                          maxHeight: '250px',
                          objectFit: 'contain',
                          borderRadius: '8px'
                        }}
                      />
                    ) : (
                      <div style={{ color: '#999', fontSize: '0.9rem' }}>No artwork preview available</div>
                    )}
                  </div>
                </div>

                {/* Usage History (Audit Trail) */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#333', margin: 0 }}>
                      Usage History
                    </h3>
                    <div style={{ fontSize: '1rem', fontWeight: '600', color: '#00BCD4' }}>
                      Total Impressions: {screenDetails.usageRecords && screenDetails.usageRecords.length > 0 
                        ? screenDetails.usageRecords.reduce((sum, record) => sum + (record.expectedImpressions || 0), 0).toLocaleString()
                        : '0'}
                    </div>
                  </div>
                  {screenDetails.usageRecords && screenDetails.usageRecords.length > 0 ? (
                    <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                      {screenDetails.usageRecords.map((record, idx) => {
                        const hasNotes = record.notes || record.supplierReason || record.qualityCheckNotes;
                        const tooltipText = [
                          record.notes && `Notes: ${record.notes}`,
                          record.supplierReason && `Supplier Reason: ${record.supplierReason}`,
                          record.qualityCheckNotes && `Quality Notes: ${record.qualityCheckNotes}`
                        ].filter(Boolean).join('\n');
                        
                        return (
                        <div key={idx} 
                          title={hasNotes ? tooltipText : ''}
                          style={{
                          background: '#fafafa',
                          padding: '10px 16px',
                          borderRadius: '8px',
                          marginBottom: '8px',
                          borderLeft: '4px solid #00BCD4',
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: '12px',
                          fontSize: '0.85rem',
                          cursor: hasNotes ? 'help' : 'default',
                          position: 'relative'
                        }}>
                          {/* Left side - Checkout info */}
                          <div>
                            <div style={{ fontWeight: '600', color: '#00BCD4', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span>{record.usageType === 'production' ? '📤 Production' : '🏭 Supplier'} - {new Date(record.checkoutDate).toLocaleDateString()}</span>
                              {hasNotes && <span style={{ fontSize: '0.9rem', color: '#FF9800' }} title="Hover to see notes">📝</span>}
                            </div>
                            {record.orderNumber && (
                              <div style={{ color: '#666', marginBottom: '2px' }}>
                                WT: {record.orderNumber}
                              </div>
                            )}
                            {record.expectedImpressions && (
                              <div style={{ color: '#666', marginBottom: '2px' }}>
                                Impressions: {record.expectedImpressions.toLocaleString()}
                              </div>
                            )}
                            {record.checkedOutBy && (
                              <div style={{ color: '#999', fontSize: '0.8rem', fontStyle: 'italic' }}>
                                By: {record.checkedOutBy}
                              </div>
                            )}
                          </div>

                          {/* Right side - Return info */}
                          <div style={{ textAlign: 'right' }}>
                            {record.checkinDate ? (
                              <>
                                <div style={{ color: '#666', marginBottom: '6px' }}>
                                  Returned: {new Date(record.checkinDate).toLocaleDateString()}
                                </div>
                                <div style={{ fontWeight: '600', color: record.qualityCheckPassed ? '#4CAF50' : '#F44336', marginBottom: '2px' }}>
                                  {record.qualityCheckPassed ? '✅ Passed' : '❌ Failed'}
                                </div>
                                {record.checkedInBy && (
                                  <div style={{ color: '#999', fontSize: '0.8rem', fontStyle: 'italic' }}>
                                    By: {record.checkedInBy}
                                  </div>
                                )}
                              </>
                            ) : (
                              <div style={{ color: '#999', fontStyle: 'italic' }}>Not yet returned</div>
                            )}
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p style={{ color: '#666', fontStyle: 'italic' }}>No usage history available</p>
                  )}
                </div>

                {/* Imposition History */}
                {screenDetails.history && screenDetails.history.length > 0 && (
                  <div style={{ marginTop: '24px' }}>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#333', marginBottom: '16px' }}>
                      Imposition History
                    </h3>
                    <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                      {screenDetails.history.map((hist, idx) => (
                        <div key={idx} style={{
                          background: '#f0f0f0',
                          padding: '10px 12px',
                          borderRadius: '8px',
                          marginBottom: '6px',
                          fontSize: '0.85rem'
                        }}>
                          <div style={{ fontWeight: '600', marginBottom: '2px' }}>
                            {hist.customerName} - {hist.itemDescription}
                          </div>
                          <div style={{ color: '#666' }}>
                            Imposed: {new Date(hist.dateImposed).toLocaleDateString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ padding: '60px', textAlign: 'center' }}>
                <p style={{ color: '#666' }}>Screen details not found</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SilkscreensPage;
